import User from 'models/user'
import { post, get, put, remove } from './api.service'
import { IChallenge } from 'models/challenge'
import Company from 'models/company'
import { dateToMysqlDateOrEmpty } from 'utils/helpers'
import confirmService from './confirm.service'
import toastService from './toast.service'
import redirect, { redirectHard } from 'utils/redirect'

const ChallengeService = {
	createChallenge(params: any) {
		const finalParams = { answers: [], ...params }
		return post('/challenges', finalParams)
	},

	updateChallenge(challengeId: string, params: any) {
		const finalParams = { answers: [], ...params }
		return put(`/challenges/${challengeId}`, finalParams)
	},

	getChallenges(params: any) {
		return get('/challenges', params)
	},

	getChallenge(challengeId: string, params) {
		return get(`/challenges/${challengeId}`, params)
	},

	updateChallengeState(challengeId: string, params: any) {
		return put(`/challenges/${challengeId}/state`, params)
	},

	deleteChallenge(challengeId: string) {
		return remove(`/challenges/${challengeId}`)
	},

	getChallengeTeams(challengeId: string) {
		return get(`/challenges/${challengeId}/teams`)
	},

	updateChallengeTeams(challengeId: string, params: any) {
		return put(`/challenges/${challengeId}/teams`, params)
	},

	prepareCompanyChallengePayloadSplitData(data) {
		return ChallengeService.prepareCompanyChallengePayload(
			data.companyUserData,
			data,
			data.companyData,
			data.organisationId,
			data.challengeSpaceId
		)
	},

	/**
	 * Helper function to prepare payload for combined createCompanyChallenge request
	 *
	 * @param   {User}       userData
	 * @param   {Challenge}  challengeData
	 * @param   {Company}    companyData
	 * @param   {string}     organisationId
	 * @param   {string}     challengeSpaceId
	 *
	 * @return  {[type]}                       Object with prepared payload
	 */
	prepareCompanyChallengePayload(
		userData: User,
		challengeData: IChallenge,
		companyData: Company,
		organisationId: string,
		challengeSpaceId: string
	) {
		return {
			userFirstname: userData.firstname,
			userLastname: userData.lastname,
			userEmail: userData.email,
			userPassword: userData.password,
			userOrganisationId: organisationId,
			userLinkedinUrl: userData.linkedinUrl,
			userPhoneNumber: userData.phoneNumber,

			answers: Object.values(challengeData.answers),
			challengeTitle: challengeData.title,
			challengeSummary: challengeData.summary,
			challengeDescription: challengeData.description,
			challengeChallengeSpaceId: challengeSpaceId,
			challengeTeamCountPreference: challengeData.teamCountPreference,
			challengeOpenChallengeType: challengeData.openChallengeType,

			companyName: companyData.name,
			companyDescription: companyData.description,
			companyWebsiteUrl: companyData.websiteUrl,
			companyLogoUrl: companyData.logoUrl,
			companyEmployeeCount: companyData.employeeCount,
			companyFoundedAt: dateToMysqlDateOrEmpty(companyData.foundedAt),
			companyIban: companyData.iban,
			companyStreetName: companyData.streetName,
			companyHouseNumber: companyData.houseNumber,
			companyHouseNumberExtension: companyData.houseNumberExtension,
			companyPostalCode: companyData.postalCode,
			companyCityName: companyData.cityName,
		}
	},

	createCompanyChallenge(params: any, options?: any) {
		const finalParams = { answers: [], ...params }
		return post('/company-challenges', finalParams, options)
	},

	updateChallengeUsers(challengeId: string, params: any) {
		return put(`/challenges/${challengeId}/users`, params)
	},

	updateChallengeMove(challengeId: string, params: any) {
		return put(`/challenges/${challengeId}/move`, params)
	},

	getChallengePrepareMove(challengeId: string, params: any) {
		return get(`/challenges/${challengeId}/prepare-move`, params)
	},

	getChallengeNote(challengeId: string) {
		return get(`/challenges/${challengeId}/note`)
	},

	updateChallengeNote(challengeId: string, params: any) {
		return put(`/challenges/${challengeId}/note`, params)
	},
}

export const challengeHandler = {
	async allowCreateChallenge(user: any, callback: Function) {
		if (!user || user.hasRole(['student'])) {
			console.log('Sorry not allowed')
			return false
		}

		if (
			user.hasRole(['company-owner']) &&
			!user?.relationships?.company?.data
		) {
			let result = await confirmService.confirm({
				title: 'Company registration needed',
				text: 'Would you like to register your company now?',
			})
			if (result.isConfirmed) {
				redirect('/account/profile?tab=company_settings')
			}
			return false
		}

		callback()
	},

	createChallenge(data, callback = null) {
		challengeHandler
			.createChallengeVia(data)
			.then((result) => {
				challengeHandler.showResultMesssage(result)
				if (callback) {
					callback(result)
				}
			})
			.catch(() => {
				toastService.showError('Something went wrong')
			})
	},

	createChallengeVia(data) {
		if (data.companyData.name) {
			return ChallengeService.createCompanyChallenge(
				ChallengeService.prepareCompanyChallengePayloadSplitData(data)
			)
		} else {
			return ChallengeService.createChallenge(data)
		}
	},

	async createChallengeAndRedirect(auth, data: any) {
		challengeHandler.createChallenge(data, function (result) {
			if (result) {
				let allowRedict = challengeHandler.showResultMesssage(result)
				if (allowRedict) {
					let challenge_id = Object.keys(result.challenge)[0]
					if (
						result.challenge[challenge_id].attributes.openChallengeType !=
						'private'
					) {
						if (auth.hasRole(['company-owner'])) {
							redirectHard('/account/challenges/')
						} else {
							redirectHard('/account/open-challenges/')
						}
					} else {
						redirectHard(
							'/account/challenge-spaces/' +
								data.challengeSpaceId +
								'?tab=challenges'
						)
					}
				}
			}
		})
	},

	showResultMesssage(result) {
		let allowRedict = true

		if (result?.verifyRepsonse) {
			let verifyRepsonseId = Object.keys(result.verifyRepsonse)[0]
			let message = result.verifyRepsonse[verifyRepsonseId].attributes.message

			allowRedict = false
			toastService.showWarning('Challenge is submitted.\n' + message, {
				timer: 15000, // 10 seconds
			})
		} else {
			toastService.showSuccess('Challenge added!')
		}
		return allowRedict
	},
}

export default ChallengeService
